import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';
import { stripePromise } from '../services/stripe';
import { useAuth } from '../features/auth';
import { useCheckout } from '../features/checkout';
import { useGacha } from '../features/gacha';
import { useLocker } from '../features/locker';
import UserLogin from './UserLogin';
import MobileLoginModal from './MobileLoginModal';
import PrizeRevealModal from './PrizeRevealModal';
import LockerFullModal from './LockerFullModal';
import LockerShippingModal from './LockerShippingModal';
import TokenIcon from './TokenIcon';

const GlobalModals = () => {
  const { loginPromptOpen } = useAuth();
  const {
    shippingRewardModal,
    dismissShippingRewardModal,
  } = useCheckout();
  const [isMobile, setIsMobile] = useState(false);
  const {
    isPrizeModalOpen,
    currentPrize,
    prizeModalOrigin,
    closePrizeModal,
    viewLockerFromPrize,
    shipLockerFromPrize,
    expandLockerFromPrize,
  } = useGacha();
  const {
    lockerDecision,
    lockerActionPending,
    shippingFlowContext,
    shippingRequestPending,
    shippingError,
    showLockerFullModal,
    expandLocker,
    shipLockerNow,
    confirmShipment,
    closeShippingFlow,
  } = useLocker();
  const navigate = useNavigate();
  const [stripeReady, setStripeReady] = useState(false);

  // Resolve stripePromise to check if it's available
  useEffect(() => {
    // CHANGED: detect mobile via matchMedia (tailwind sm breakpoint ~640px)
    const mq = typeof window !== 'undefined' ? window.matchMedia('(max-width: 640px)') : null;
    const update = () => setIsMobile(Boolean(mq?.matches));
    update();
    mq?.addEventListener?.('change', update);
    return () => mq?.removeEventListener?.('change', update);
  }, []);

  useEffect(() => {
    if (stripePromise) {
      stripePromise
        .then((stripe) => {
          setStripeReady(stripe !== null);
        })
        .catch(() => {
          setStripeReady(false);
        });
    } else {
      setStripeReady(false);
    }
  }, []);

  const handleViewLocker = () => {
    viewLockerFromPrize();
    navigate('/locker');
  };

  // Welcome bonus (token prize): close modal, go to homepage, and refresh
  const handleBackToHomepage = () => {
    closePrizeModal();
    navigate('/', { replace: true });
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  };

  const handleShipLocker = () => {
    shipLockerFromPrize();
    shipLockerNow();
  };

  const handleExpandLocker = () => {
    expandLockerFromPrize();
    expandLocker();
  };

  const handleRewardBackToLocker = () => {
    dismissShippingRewardModal();
    navigate('/locker');
  };

  const handleRewardUseTokens = () => {
    dismissShippingRewardModal();
    navigate('/');
  };

  const lockerSummary = useMemo(() => {
    if (!lockerDecision) {
      return null;
    }
    return {
      previewItems: lockerDecision.lockerItems || [],
      filled: lockerDecision.lockerItems?.length || 0,
      max: lockerDecision.maxSlots || 8,
    };
  }, [lockerDecision]);

  const lockerExpansionLimitReached = useMemo(() => {
    if (!lockerDecision?.maxSlots) {
      return false;
    }
    // Max expansion is typically 8 base + 4 expansion = 12
    return lockerDecision.maxSlots >= 12;
  }, [lockerDecision?.maxSlots]);

  return (
    <>
      {loginPromptOpen && (
        isMobile ? (
          <MobileLoginModal onClose={() => { /* mobile modal handles its own close via Auth */ }} />
        ) : (
          <div className="fixed inset-0 z-[70] flex items-center justify-center bg-ponpon-navy/30 px-4 py-10 backdrop-blur-sm">
            <div className="w-full max-w-5xl">
              <UserLogin />
            </div>
          </div>
        )
      )}

      <PrizeRevealModal
        isOpen={isPrizeModalOpen && !!currentPrize && !lockerDecision}
        prize={currentPrize}
        onClose={closePrizeModal}
        onViewLocker={handleViewLocker}
        onBackToHomepage={handleBackToHomepage}
        originOffset={prizeModalOrigin}
      />

      <LockerFullModal
        isOpen={(isPrizeModalOpen && !!lockerDecision?.pendingPrize) || (showLockerFullModal && !!lockerDecision?.pendingPrize)}
        prize={lockerDecision?.pendingPrize?.prize || lockerDecision?.pendingPrize || null}
        lockerSummary={lockerSummary}
        onShipLocker={handleShipLocker}
        onExpandLocker={handleExpandLocker}
        lockerActionPending={lockerActionPending}
        shippingPending={shippingRequestPending}
        lockerExpansionLimitReached={lockerExpansionLimitReached}
        originOffset={prizeModalOrigin}
        isMobile={isMobile}
      />

      {shippingFlowContext && stripeReady && stripePromise && (
        <Elements
          stripe={stripePromise}
          options={{
            appearance: {
              theme: 'flat',
              variables: {
                fontSizeBase: '14px',
                fontSizeSm: '12px',
                spacingUnit: '4px',
                borderRadius: '12px',
                colorPrimary: '#E23A32',
                colorBackground: '#ffffff',
                colorText: '#1E2333',
                colorTextSecondary: '#1E2333',
                colorTextPlaceholder: 'rgba(30, 35, 51, 0.45)',
                colorBorder: 'rgba(30, 35, 51, 0.15)',
                colorDanger: '#E23A32',
              },
              rules: {
                '.Label': {
                  fontSize: '12px',
                  fontWeight: '600',
                  color: 'rgba(30, 35, 51, 0.6)',
                  marginBottom: '4px',
                },
                '.Input': {
                  fontSize: '14px',
                  padding: '8px 12px',
                  borderRadius: '12px',
                  border: '1px solid rgba(30, 35, 51, 0.15)',
                  backgroundColor: '#ffffff',
                  color: '#1E2333',
                },
                '.Input:focus': {
                  borderColor: '#E23A32',
                  boxShadow: '0 0 0 2px rgba(226, 58, 50, 0.2)',
                },
                '.Error': {
                  fontSize: '12px',
                  color: '#E23A32',
                },
              },
            },
          }}
        >
          <LockerShippingModal
            isOpen={!!shippingFlowContext}
            origin={shippingFlowContext.origin}
            items={shippingFlowContext.items || []}
            pendingPrize={shippingFlowContext.pendingPrize}
            maxLockerSlots={shippingFlowContext.maxLockerSlots || 8}
            shippingAddress={shippingFlowContext.shippingAddress}
            onClose={closeShippingFlow}
            onConfirm={confirmShipment}
            loading={shippingRequestPending}
            error={shippingError}
          />
        </Elements>
      )}
      {shippingFlowContext && !stripeReady && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ponpon-navy/40 px-4 py-10 backdrop-blur-sm">
          <div className="relative w-full max-w-3xl rounded-3xl border border-white/70 bg-white/95 p-6 shadow-2xl">
            <p className="text-sm text-ponpon-navy/70">
              Stripe is not available. Please check your configuration or use manual address entry.
            </p>
            <button
              type="button"
              onClick={closeShippingFlow}
              className="mt-4 rounded-full bg-ponpon-coral px-4 py-2 text-sm font-semibold text-white"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {shippingRewardModal && (
        <div
          className="fixed inset-0 z-[72] flex items-center justify-center bg-ponpon-navy/40 px-4 py-10 backdrop-blur-sm"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              dismissShippingRewardModal();
            }
          }}
        >
          <div
            className="relative w-full max-w-md overflow-hidden rounded-3xl border border-white/70 bg-white/95 p-6 text-center shadow-[0_22px_55px_rgba(30,35,51,0.3)]"
            role="dialog"
            aria-modal="true"
            aria-labelledby="shipping-reward-title"
          >
            <div className="mx-auto mb-4 flex h-24 w-24 items-center justify-center rounded-full bg-ponpon-gold/15">
              <TokenIcon className="h-16 w-16" alt="PonPon token" />
            </div>

            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-ponpon-coral">
              {shippingRewardModal.rewardKind === 'collection'
                ? 'Collection complete'
                : 'Shipping confirmed'}
            </p>
            <h2
              id="shipping-reward-title"
              className="mt-2 text-2xl font-extrabold text-ponpon-navy"
            >
              You earned {shippingRewardModal.tokensAwarded} {shippingRewardModal.tokensAwarded === 1 ? 'token' : 'tokens'}
            </h2>

            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              <button
                type="button"
                onClick={handleRewardBackToLocker}
                className="inline-flex items-center justify-center rounded-full border border-ponpon-navy/15 bg-white px-4 py-3 text-sm font-semibold text-ponpon-navy transition hover:bg-ponpon-navy/5"
              >
                Back to Locker
              </button>
              <button
                type="button"
                onClick={handleRewardUseTokens}
                className="inline-flex items-center justify-center rounded-full bg-ponpon-coral px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-ponpon-coral/90"
              >
                Use Tokens
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default GlobalModals;
